import type {
  AssistAcknowledgeResponse,
  AssistReportResponse,
  CalculationDetailResponse,
  CalculationListResponse,
  ChargesPayload,
  ChargesResponse,
  CodingOutPayload,
  CodingOutResponse,
  CodingOutStatusResponse,
  CreateLossResponse,
  CreateSelfEmploymentPeriodResponse,
  CrystallisationObligationsResponse,
  DeleteLossResponse,
  DividendsIncomePayload,
  DividendsIncomeResponse,
  EmploymentFinancialDetailsPayload,
  EmploymentFinancialDetailsResponse,
  EmploymentIncomeListResponse,
  EmploymentIncomePayload,
  EmploymentIncomeResponse,
  ExpensesPayload,
  ExpensesResponse,
  ForeignIncomePayload,
  ForeignIncomeResponse,
  GetBusinessDetailsResponse,
  GetBusinessListResponse,
  GetLossResponse,
  GetSelfEmploymentPeriodResponse,
  IncomeAndExpenditureObligationsResponse,
  OtherDisposalPayload,
  OtherDisposalResponse,
  OtherEmploymentIncomePayload,
  OtherIncomePayload,
  OtherIncomeResponse,
  ListLossesResponse,
  LossClaimOrderPayload,
  LossClaimOrderResponse,
  LossClaimPayload,
  LossClaimResponse,
  LossClaimsListResponse,
  LossPayload,
  ListSelfEmploymentPeriodsResponse,
  PensionsIncomePayload,
  PensionsIncomeResponse,
  PropertyAnnualPayload,
  PropertyAnnualResponse,
  PropertyPeriodPayload,
  PropertyPeriodResponse,
  PropertyPeriodListResponse,
  PpdDisposalListResponse,
  PpdDisposalPayload,
  PpdDisposalResponse,
  ReliefsPayload,
  ReliefsResponse,
  SelfAssessmentBalanceResponse,
  SelfAssessmentPaymentsResponse,
  SelfEmploymentAnnualPayload,
  SelfEmploymentAnnualResponse,
  SelfEmploymentPeriodPayload,
  SelfEmploymentPeriodSummary,
  TriggerCalculationResponse,
  SavingsIncomePayload,
  SavingsIncomeResponse,
  UpdateLossResponse,
  UpdateSelfEmploymentPeriodPayload,
  UpdateSelfEmploymentPeriodResponse,
} from '../types'
import { api } from './api'

class SelfAssessmentService {
  private readonly basePath = '/filling/self-assessment'

  // ----------------------------------------------------------
  // Assessment Record Management
  // ----------------------------------------------------------

  /**
   * Start a new self-assessment by creating an assessment record
   * This MUST be called first before submitting any income data
   */
  async startSelfAssessment(
    nino: string,
    planSlug: string
  ): Promise<{
    response: {
      success: boolean
      message: string
      data: {
        assessmentId: string
        nino: string
        planId: string
        user: string
        createdAt: string
      }
    }
  }> {
    return await api.post<any>(
      `${this.basePath}/self-employment/${nino}/start-self-assessment`,
      { planSlug }
    )
  }

  /**
   * Get a specific assessment record by ID
   */
  async getAssessment(assessmentId: string): Promise<{
    response: {
      success: boolean
      data: any
    }
  }> {
    return await api.get<any>(`${this.basePath}/${assessmentId}`, { skipAuthLogout: true })
  }

  /**
   * List all assessments for the current user
   */
  async listAssessments(params?: {
    nino?: string
    taxYear?: string
  }): Promise<{
    response: {
      success: boolean
      data: any[]
    }
  }> {
    const queryParams = new URLSearchParams()
    if (params?.nino) queryParams.append('nino', params.nino)
    if (params?.taxYear) queryParams.append('taxYear', params.taxYear)
    
    const queryString = queryParams.toString()
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath
    
    return await api.get<any>(url, { skipAuthLogout: true })
  }

  /**
   * Fetch all self-assessments for the current user
   * GET /api/filling/self-assessment
   */
  async getAllSelfAssessments(): Promise<{
    statusCode: number
    response: {
      success: boolean
      data: any[]
      message?: string
    }
  }> {
    return await api.get<{
      success: boolean
      data: any[]
      message?: string
    }>('/filling/self-assessment', { skipAuthLogout: true })
  }

  // ----------------------------------------------------------
  // Business details
  // ----------------------------------------------------------

  async listBusinesses(nino: string): Promise<GetBusinessListResponse> {
    return await api.get<GetBusinessListResponse['response']>(
      `${this.basePath}/business-details/${nino}/list`
    )
  }

  async getBusinessDetails(
    nino: string,
    businessId: string
  ): Promise<GetBusinessDetailsResponse> {
    return await api.get<GetBusinessDetailsResponse['response']>(
      `${this.basePath}/business-details/${nino}/${businessId}`
    )
  }

  // ----------------------------------------------------------
  // Self-employment
  // ----------------------------------------------------------

  async createSelfEmploymentPeriod(
    nino: string,
    businessId: string,
    payload: SelfEmploymentPeriodPayload
  ): Promise<CreateSelfEmploymentPeriodResponse> {
    return await api.post<SelfEmploymentPeriodSummary>(
      `${this.basePath}/self-employment/${nino}/${businessId}/period`,
      payload
    )
  }

  async updateSelfEmploymentPeriod(
    nino: string,
    businessId: string,
    taxYear: string,
    periodId: string,
    payload: UpdateSelfEmploymentPeriodPayload
  ): Promise<UpdateSelfEmploymentPeriodResponse> {
    return await api.put<SelfEmploymentPeriodSummary>(
      `${this.basePath}/self-employment/${nino}/${businessId}/period/${taxYear}/${periodId}`,
      payload
    )
  }

  async getSelfEmploymentPeriod(
    nino: string,
    businessId: string,
    taxYear: string,
    periodId: string
  ): Promise<GetSelfEmploymentPeriodResponse> {
    return await api.get<GetSelfEmploymentPeriodResponse['response']>(
      `${this.basePath}/self-employment/${nino}/${businessId}/period/${taxYear}/${periodId}`
    )
  }

  async listSelfEmploymentPeriods(
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<ListSelfEmploymentPeriodsResponse> {
    return await api.get<ListSelfEmploymentPeriodsResponse['response']>(
      `${this.basePath}/self-employment/${nino}/${businessId}/period/${taxYear}`
    )
  }

  async upsertSelfEmploymentAnnual(
    nino: string,
    businessId: string,
    taxYear: string,
    payload: SelfEmploymentAnnualPayload
  ): Promise<SelfEmploymentAnnualResponse> {
    return await api.put<SelfEmploymentAnnualResponse['response']>(
      `${this.basePath}/self-employment/${nino}/${businessId}/annual/${taxYear}`,
      payload
    )
  }

  async getSelfEmploymentAnnual(
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<SelfEmploymentAnnualResponse> {
    return await api.get<SelfEmploymentAnnualResponse['response']>(
      `${this.basePath}/self-employment/${nino}/${businessId}/annual/${taxYear}`
    )
  }

  async deleteSelfEmploymentAnnual(
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<SelfEmploymentAnnualResponse> {
    return await api.delete<SelfEmploymentAnnualResponse['response']>(
      `${this.basePath}/self-employment/${nino}/${businessId}/annual/${taxYear}`
    )
  }

  // ----------------------------------------------------------
  // Property business (UK & Foreign)
  // ----------------------------------------------------------

  private propertyBase(scope: 'uk' | 'foreign'): string {
    return `${this.basePath}/property-business/${scope}`
  }

  async createPropertyPeriod(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string,
    payload: PropertyPeriodPayload
  ): Promise<PropertyPeriodResponse> {
    return await api.post<PropertyPeriodResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/period/${taxYear}`,
      payload
    )
  }

  async updatePropertyPeriod(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string,
    submissionId: string,
    payload: PropertyPeriodPayload
  ): Promise<PropertyPeriodResponse> {
    return await api.put<PropertyPeriodResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/period/${taxYear}/${submissionId}`,
      payload
    )
  }

  async getPropertyPeriod(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string,
    submissionId: string
  ): Promise<PropertyPeriodResponse> {
    return await api.get<PropertyPeriodResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/period/${taxYear}/${submissionId}`
    )
  }

  async listPropertyPeriods(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<PropertyPeriodListResponse> {
    return await api.get<PropertyPeriodListResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/period/${taxYear}`
    )
  }

  async upsertPropertyAnnual(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string,
    payload: PropertyAnnualPayload
  ): Promise<PropertyAnnualResponse> {
    return await api.put<PropertyAnnualResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/annual/${taxYear}`,
      payload
    )
  }

  async getPropertyAnnual(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<PropertyAnnualResponse> {
    return await api.get<PropertyAnnualResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/annual/${taxYear}`
    )
  }

  async deletePropertyAnnual(
    scope: 'uk' | 'foreign',
    nino: string,
    businessId: string,
    taxYear: string
  ): Promise<PropertyAnnualResponse> {
    return await api.delete<PropertyAnnualResponse['response']>(
      `${this.propertyBase(scope)}/${nino}/${businessId}/annual/${taxYear}`
    )
  }

  // ----------------------------------------------------------
  // Employment income
  // ----------------------------------------------------------

  async listEmployments(
    nino: string,
    taxYear: string
  ): Promise<EmploymentIncomeListResponse> {
    return await api.get<EmploymentIncomeListResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}`
    )
  }

  async createEmployment(
    nino: string,
    taxYear: string,
    payload: EmploymentIncomePayload
  ): Promise<EmploymentIncomeResponse> {
    return await api.post<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}`,
      payload
    )
  }

  async updateEmployment(
    nino: string,
    taxYear: string,
    employmentId: string,
    payload: EmploymentIncomePayload
  ): Promise<EmploymentIncomeResponse> {
    return await api.put<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}`,
      payload
    )
  }

  async getEmployment(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentIncomeResponse> {
    return await api.get<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}`
    )
  }

  async deleteEmployment(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentIncomeResponse> {
    return await api.delete<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}`
    )
  }

  async upsertEmploymentFinancialDetails(
    nino: string,
    taxYear: string,
    employmentId: string,
    payload: EmploymentFinancialDetailsPayload
  ): Promise<EmploymentFinancialDetailsResponse> {
    return await api.put<EmploymentFinancialDetailsResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/financial-details`,
      payload
    )
  }

  async getEmploymentFinancialDetails(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentFinancialDetailsResponse> {
    return await api.get<EmploymentFinancialDetailsResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/financial-details`
    )
  }

  async deleteEmploymentFinancialDetails(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentFinancialDetailsResponse> {
    return await api.delete<EmploymentFinancialDetailsResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/financial-details`
    )
  }

  async upsertOtherEmploymentIncome(
    nino: string,
    taxYear: string,
    employmentId: string,
    payload: OtherEmploymentIncomePayload
  ): Promise<EmploymentIncomeResponse> {
    return await api.put<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/other`,
      payload
    )
  }

  async ignoreEmployment(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentIncomeResponse> {
    return await api.put<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/ignore`
    )
  }

  async unignoreEmployment(
    nino: string,
    taxYear: string,
    employmentId: string
  ): Promise<EmploymentIncomeResponse> {
    return await api.delete<EmploymentIncomeResponse['response']>(
      `${this.basePath}/employments-income/${nino}/${taxYear}/${employmentId}/ignore`
    )
  }

  // ----------------------------------------------------------
  // Other income sources
  // ----------------------------------------------------------

  async upsertDividendsIncome(
    nino: string,
    taxYear: string,
    payload: DividendsIncomePayload
  ): Promise<DividendsIncomeResponse> {
    return await api.put<DividendsIncomeResponse['response']>(
      `${this.basePath}/dividends-income/${nino}/${taxYear}`,
      payload
    )
  }

  async getDividendsIncome(
    nino: string,
    taxYear: string
  ): Promise<DividendsIncomeResponse> {
    return await api.get<DividendsIncomeResponse['response']>(
      `${this.basePath}/dividends-income/${nino}/${taxYear}`
    )
  }

  async deleteDividendsIncome(
    nino: string,
    taxYear: string
  ): Promise<DividendsIncomeResponse> {
    return await api.delete<DividendsIncomeResponse['response']>(
      `${this.basePath}/dividends-income/${nino}/${taxYear}`
    )
  }

  async upsertSavingsIncome(
    nino: string,
    taxYear: string,
    payload: SavingsIncomePayload
  ): Promise<SavingsIncomeResponse> {
    return await api.put<SavingsIncomeResponse['response']>(
      `${this.basePath}/savings-income/${nino}/${taxYear}`,
      payload
    )
  }

  async getSavingsIncome(
    nino: string,
    taxYear: string
  ): Promise<SavingsIncomeResponse> {
    return await api.get<SavingsIncomeResponse['response']>(
      `${this.basePath}/savings-income/${nino}/${taxYear}`
    )
  }

  async deleteSavingsIncome(
    nino: string,
    taxYear: string
  ): Promise<SavingsIncomeResponse> {
    return await api.delete<SavingsIncomeResponse['response']>(
      `${this.basePath}/savings-income/${nino}/${taxYear}`
    )
  }

  async upsertPensionsIncome(
    nino: string,
    taxYear: string,
    payload: PensionsIncomePayload
  ): Promise<PensionsIncomeResponse> {
    return await api.put<PensionsIncomeResponse['response']>(
      `${this.basePath}/pensions-income/${nino}/${taxYear}`,
      payload
    )
  }

  async getPensionsIncome(
    nino: string,
    taxYear: string
  ): Promise<PensionsIncomeResponse> {
    return await api.get<PensionsIncomeResponse['response']>(
      `${this.basePath}/pensions-income/${nino}/${taxYear}`
    )
  }

  async deletePensionsIncome(
    nino: string,
    taxYear: string
  ): Promise<PensionsIncomeResponse> {
    return await api.delete<PensionsIncomeResponse['response']>(
      `${this.basePath}/pensions-income/${nino}/${taxYear}`
    )
  }

  async upsertOtherIncome(
    nino: string,
    taxYear: string,
    payload: OtherIncomePayload
  ): Promise<OtherIncomeResponse> {
    return await api.put<OtherIncomeResponse['response']>(
      `${this.basePath}/other-income/${nino}/${taxYear}`,
      payload
    )
  }

  async getOtherIncome(
    nino: string,
    taxYear: string
  ): Promise<OtherIncomeResponse> {
    return await api.get<OtherIncomeResponse['response']>(
      `${this.basePath}/other-income/${nino}/${taxYear}`
    )
  }

  async deleteOtherIncome(
    nino: string,
    taxYear: string
  ): Promise<OtherIncomeResponse> {
    return await api.delete<OtherIncomeResponse['response']>(
      `${this.basePath}/other-income/${nino}/${taxYear}`
    )
  }

  async upsertForeignIncome(
    nino: string,
    taxYear: string,
    payload: ForeignIncomePayload
  ): Promise<ForeignIncomeResponse> {
    return await api.put<ForeignIncomeResponse['response']>(
      `${this.basePath}/foreign-income/${nino}/${taxYear}`,
      payload
    )
  }

  async getForeignIncome(
    nino: string,
    taxYear: string
  ): Promise<ForeignIncomeResponse> {
    return await api.get<ForeignIncomeResponse['response']>(
      `${this.basePath}/foreign-income/${nino}/${taxYear}`
    )
  }

  async deleteForeignIncome(
    nino: string,
    taxYear: string
  ): Promise<ForeignIncomeResponse> {
    return await api.delete<ForeignIncomeResponse['response']>(
      `${this.basePath}/foreign-income/${nino}/${taxYear}`
    )
  }

  // ----------------------------------------------------------
  // Obligations
  // ----------------------------------------------------------

  async getIncomeAndExpenditureObligations(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<IncomeAndExpenditureObligationsResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<
      IncomeAndExpenditureObligationsResponse['response']
    >(`${this.basePath}/obligations/${nino}/income-and-expenditure${suffix}`)
  }

  async getCrystallisationObligations(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<CrystallisationObligationsResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<CrystallisationObligationsResponse['response']>(
      `${this.basePath}/obligations/${nino}/crystallisation${suffix}`
    )
  }

  // ----------------------------------------------------------
  // Losses
  // ----------------------------------------------------------

  async createLoss(
    nino: string,
    payload: LossPayload
  ): Promise<CreateLossResponse> {
    return await api.post<CreateLossResponse['response']>(
      `${this.basePath}/losses/${nino}/brought-forward`,
      payload
    )
  }

  async updateLoss(
    nino: string,
    lossId: string,
    payload: Pick<LossPayload, 'lossAmount'>
  ): Promise<UpdateLossResponse> {
    return await api.put<UpdateLossResponse['response']>(
      `${this.basePath}/losses/${nino}/brought-forward/${lossId}`,
      payload
    )
  }

  async getLoss(
    nino: string,
    lossId: string
  ): Promise<GetLossResponse> {
    return await api.get<GetLossResponse['response']>(
      `${this.basePath}/losses/${nino}/brought-forward/${lossId}`
    )
  }

  async deleteLoss(
    nino: string,
    lossId: string
  ): Promise<DeleteLossResponse> {
    return await api.delete<DeleteLossResponse['response']>(
      `${this.basePath}/losses/${nino}/brought-forward/${lossId}`
    )
  }

  async listLosses(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<ListLossesResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<ListLossesResponse['response']>(
      `${this.basePath}/losses/${nino}/brought-forward${suffix}`
    )
  }

  async createLossClaim(
    nino: string,
    taxYearClaimedFor: string,
    payload: LossClaimPayload
  ): Promise<LossClaimResponse> {
    return await api.post<LossClaimResponse['response']>(
      `${this.basePath}/losses/${nino}/claims/${taxYearClaimedFor}`,
      payload
    )
  }

  async updateLossClaimType(
    nino: string,
    claimId: string,
    payload: Pick<LossClaimPayload, 'typeOfClaim'>
  ): Promise<LossClaimResponse> {
    return await api.post<LossClaimResponse['response']>(
      `${this.basePath}/losses/${nino}/claims/${claimId}/change-type`,
      payload
    )
  }

  async getLossClaim(
    nino: string,
    claimId: string
  ): Promise<LossClaimResponse> {
    return await api.get<LossClaimResponse['response']>(
      `${this.basePath}/losses/${nino}/claims/${claimId}`
    )
  }

  async deleteLossClaim(
    nino: string,
    claimId: string
  ): Promise<DeleteLossResponse> {
    return await api.delete<DeleteLossResponse['response']>(
      `${this.basePath}/losses/${nino}/claims/${claimId}`
    )
  }

  async listLossClaims(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<LossClaimsListResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<LossClaimsListResponse['response']>(
      `${this.basePath}/losses/${nino}/claims${suffix}`
    )
  }

  async orderLossClaims(
    nino: string,
    taxYearClaimedFor: string,
    payload: LossClaimOrderPayload
  ): Promise<LossClaimOrderResponse> {
    return await api.put<LossClaimOrderResponse['response']>(
      `${this.basePath}/losses/${nino}/claims/order/${taxYearClaimedFor}`,
      payload
    )
  }

  // ----------------------------------------------------------
  // Deductions & adjustments
  // ----------------------------------------------------------

  async upsertExpenses(
    nino: string,
    taxYear: string,
    payload: ExpensesPayload
  ): Promise<ExpensesResponse> {
    return await api.put<ExpensesResponse['response']>(
      `${this.basePath}/expenses/${nino}/${taxYear}`,
      payload
    )
  }

  async getExpenses(
    nino: string,
    taxYear: string
  ): Promise<ExpensesResponse> {
    return await api.get<ExpensesResponse['response']>(
      `${this.basePath}/expenses/${nino}/${taxYear}`
    )
  }

  async deleteExpenses(
    nino: string,
    taxYear: string
  ): Promise<ExpensesResponse> {
    return await api.delete<ExpensesResponse['response']>(
      `${this.basePath}/expenses/${nino}/${taxYear}`
    )
  }

  async upsertReliefs(
    nino: string,
    taxYear: string,
    payload: ReliefsPayload
  ): Promise<ReliefsResponse> {
    return await api.put<ReliefsResponse['response']>(
      `${this.basePath}/reliefs/${nino}/${taxYear}`,
      payload
    )
  }

  async getReliefs(
    nino: string,
    taxYear: string
  ): Promise<ReliefsResponse> {
    return await api.get<ReliefsResponse['response']>(
      `${this.basePath}/reliefs/${nino}/${taxYear}`
    )
  }

  async deleteReliefs(
    nino: string,
    taxYear: string
  ): Promise<ReliefsResponse> {
    return await api.delete<ReliefsResponse['response']>(
      `${this.basePath}/reliefs/${nino}/${taxYear}`
    )
  }

  async upsertCharges(
    nino: string,
    taxYear: string,
    payload: ChargesPayload
  ): Promise<ChargesResponse> {
    return await api.put<ChargesResponse['response']>(
      `${this.basePath}/charges/${nino}/${taxYear}`,
      payload
    )
  }

  async getCharges(
    nino: string,
    taxYear: string
  ): Promise<ChargesResponse> {
    return await api.get<ChargesResponse['response']>(
      `${this.basePath}/charges/${nino}/${taxYear}`
    )
  }

  async deleteCharges(
    nino: string,
    taxYear: string
  ): Promise<ChargesResponse> {
    return await api.delete<ChargesResponse['response']>(
      `${this.basePath}/charges/${nino}/${taxYear}`
    )
  }

  // ----------------------------------------------------------
  // Self Assessment accounts
  // ----------------------------------------------------------

  private accountsBase = `${this.basePath}/self-assessment`

  async getBalanceAndTransactions(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<SelfAssessmentBalanceResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<SelfAssessmentBalanceResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/balance-and-transactions${suffix}`
    )
  }

  async getPaymentsAndAllocations(
    nino: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<SelfAssessmentPaymentsResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<SelfAssessmentPaymentsResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/payments-and-allocations${suffix}`
    )
  }

  async getChargeDetails(
    nino: string,
    transactionId: string
  ): Promise<SelfAssessmentPaymentsResponse> {
    return await api.get<SelfAssessmentPaymentsResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/charges/${transactionId}`
    )
  }

  async getCodingOut(
    nino: string,
    taxYear: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<CodingOutResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<CodingOutResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/${taxYear}/coding-out${suffix}`
    )
  }

  async upsertCodingOut(
    nino: string,
    taxYear: string,
    payload: CodingOutPayload
  ): Promise<CodingOutResponse> {
    return await api.put<CodingOutResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/${taxYear}/coding-out`,
      payload
    )
  }

  async deleteCodingOut(
    nino: string,
    taxYear: string
  ): Promise<CodingOutResponse> {
    return await api.delete<CodingOutResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/${taxYear}/coding-out`
    )
  }

  async getCodingOutStatus(
    nino: string,
    taxYear: string
  ): Promise<CodingOutStatusResponse> {
    return await api.get<CodingOutStatusResponse['response']>(
      `${this.accountsBase}/accounts/${nino}/${taxYear}/coding-out-status`
    )
  }

  // ----------------------------------------------------------
  // Capital gains
  // ----------------------------------------------------------

  async createPpdDisposal(
    nino: string,
    taxYear: string,
    payload: PpdDisposalPayload
  ): Promise<PpdDisposalResponse> {
    return await api.post<PpdDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/ppd`,
      payload
    )
  }

  async updatePpdDisposal(
    nino: string,
    taxYear: string,
    disposalId: string,
    payload: PpdDisposalPayload
  ): Promise<PpdDisposalResponse> {
    return await api.put<PpdDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/ppd/${disposalId}`,
      payload
    )
  }

  async getPpdDisposal(
    nino: string,
    taxYear: string,
    disposalId: string
  ): Promise<PpdDisposalResponse> {
    return await api.get<PpdDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/ppd/${disposalId}`
    )
  }

  async deletePpdDisposal(
    nino: string,
    taxYear: string,
    disposalId: string
  ): Promise<PpdDisposalResponse> {
    return await api.delete<PpdDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/ppd/${disposalId}`
    )
  }

  async listPpdDisposals(
    nino: string,
    taxYear: string
  ): Promise<PpdDisposalListResponse> {
    return await api.get<PpdDisposalListResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/ppd`
    )
  }

  async upsertOtherDisposal(
    nino: string,
    taxYear: string,
    payload: OtherDisposalPayload
  ): Promise<OtherDisposalResponse> {
    return await api.put<OtherDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/other`,
      payload
    )
  }

  async getOtherDisposal(
    nino: string,
    taxYear: string
  ): Promise<OtherDisposalResponse> {
    return await api.get<OtherDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/other`
    )
  }

  async deleteOtherDisposal(
    nino: string,
    taxYear: string
  ): Promise<OtherDisposalResponse> {
    return await api.delete<OtherDisposalResponse['response']>(
      `${this.basePath}/capital-gains/${nino}/${taxYear}/disposals/other`
    )
  }

  async triggerCalculation(
    nino: string,
    taxYear: string,
    payload: { assessmentId?: string; finalDeclaration?: boolean } = {}
  ): Promise<TriggerCalculationResponse> {
    return await api.post<TriggerCalculationResponse['response']>(
      `${this.accountsBase}/calculations/${nino}/${taxYear}/trigger`,
      payload
    )
  }

  async submitFinalDeclaration(
    nino: string,
    taxYear: string
  ): Promise<TriggerCalculationResponse> {
    return await this.triggerCalculation(nino, taxYear, { finalDeclaration: true })
  }

  async listCalculations(
    nino: string,
    taxYear: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<CalculationListResponse> {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const suffix = params.toString() ? `?${params.toString()}` : ''

    return await api.get<CalculationListResponse['response']>(
      `${this.accountsBase}/calculations/${nino}/${taxYear}${suffix}`
    )
  }

  async getCalculation(
    nino: string,
    taxYear: string,
    calculationId: string
  ): Promise<CalculationDetailResponse> {
    return await api.get<CalculationDetailResponse['response']>(
      `${this.accountsBase}/calculations/${nino}/${taxYear}/${calculationId}`
    )
  }

  async createAssistReport(
    nino: string,
    taxYear: string,
    calculationId: string
  ): Promise<AssistReportResponse> {
    return await api.post<AssistReportResponse['response']>(
      `${this.accountsBase}/assist/${nino}/${taxYear}/${calculationId}/report`
    )
  }

  async acknowledgeAssistReport(
    nino: string,
    reportId: string,
    correlationId: string
  ): Promise<AssistAcknowledgeResponse> {
    return await api.post<AssistAcknowledgeResponse['response']>(
      `${this.accountsBase}/assist/${nino}/${reportId}/${correlationId}/acknowledge`
    )
  }
}

export const selfAssessmentService = new SelfAssessmentService()
export default selfAssessmentService
